import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import {
  iosNativeCenterHeaderLeft,
  iosNativeContent,
  iosNativePageRoot,
  iosNativeScreenSubtitle,
  iosNativeScreenTitle,
} from '../styles/iosNativePageCss';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Droplet, Plus, Minus, Apple, Fish, Salad, ThumbsUp, ThumbsDown, ChefHat, Calendar, Trash2, ArrowRight } from 'lucide-react';
import type { DietPlan } from '../services/scanService';
import { useAWSAuth } from '../contexts/AWSAuthContext';
import { listUserScans } from '../services/scanService';
import { useSubscription } from '../hooks/useSubscription';
import { FeatureLock } from '../components/FeatureLock';
import { generateSkinLovingFoods, type SkinLovingFood } from '../utils/skinLovingFoods';
import { generateFoodsToReduce, type FoodToReduce } from '../utils/foodsToReduce';
import { getScanDisplayName } from '../utils/scanUtils';

/** Create minimal recipe fallback so every food shows View Recipe */
function getRecipeForDisplay(food: { name: string; description?: string; recipe?: { title?: string; ingredients?: string[]; steps?: string[]; servings?: number; caloriesPerServing?: number } }) {
  if (food.recipe && (food.recipe.ingredients?.length || food.recipe.steps?.length)) {
    return { title: food.recipe.title || food.name, ingredients: food.recipe.ingredients || [], steps: food.recipe.steps || [], servings: food.recipe.servings ?? 2, caloriesPerServing: food.recipe.caloriesPerServing };
  }
  return {
    title: food.name,
    ingredients: ['Whole grains or vegetable base', 'Fresh vegetables', 'Olive oil or healthy fat', 'Herbs and spices', 'Salt and pepper to taste'],
    steps: ['Review the description above for key ingredients.', 'Use whole grains and vegetables where possible.', 'Prepare using baking, grilling, or steaming instead of frying.', 'Season to taste and serve.'],
    servings: 2,
    caloriesPerServing: 350,
  };
}

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: linear-gradient(180deg, ${theme.colors.background.secondary} 0%, ${theme.colors.background.primary} 100%);
  padding: 2rem 1rem 4rem;

  ${iosNativePageRoot}
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;

  ${iosNativeContent}
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  animation: slideUp 0.5s ease-out;

  ${iosNativeCenterHeaderLeft}
`;

const Title = styled.h1`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;

  ${iosNativeScreenTitle}
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.text.secondary};
  max-width: 600px;
  margin: 0 auto 1rem;

  body.native-app & {
    margin-left: 0;
  }

  ${iosNativeScreenSubtitle}
`;

const Disclaimer = styled.div`
  background: ${theme.colors.accent.peach}30;
  padding: 1rem 1.5rem;
  border-radius: ${theme.borderRadius.lg};
  border-left: 4px solid ${theme.colors.accent.peach};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  max-width: 600px;
  margin: 0 auto;
`;

const HydrationCard = styled(Card)`
  background: linear-gradient(135deg, ${theme.colors.glow.mint} 0%, white 100%);
  animation: slideUp 0.6s ease-out;
  margin-bottom: 2rem;
`;

const HydrationHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const HydrationTitle = styled.h2`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  svg {
    color: ${theme.colors.primary[500]};
  }
`;

const GlassContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 2rem;
`;

const GlassIcon = styled.div<{ $filled: boolean }>`
  width: 40px;
  height: 50px;
  background: ${props => props.$filled ? theme.colors.primary[400] : theme.colors.border.light};
  border-radius: ${theme.borderRadius.base};
  border-bottom-left-radius: ${theme.borderRadius.lg};
  border-bottom-right-radius: ${theme.borderRadius.lg};
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;

  &:hover {
    transform: translateY(-4px);
  }

  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -3px;
    right: -3px;
    height: 10px;
    background: ${props => props.$filled ? theme.colors.primary[400] : theme.colors.border.light};
    border-radius: ${theme.borderRadius.full};
  }
`;

const HydrationStats = styled.div`
  display: flex;
  justify-content: space-around;
  padding-top: 1.5rem;
  border-top: 1px solid ${theme.colors.border.light};
`;

const Stat = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary[600]};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  margin-top: 0.25rem;
`;

const HydrationControls = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const ContentGrid = styled.div`
  display: grid;
  gap: 1.5rem;

  @media (min-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const SectionCard = styled(Card)`
  animation: slideUp 0.7s ease-out;
`;

const SectionTitle = styled.h3`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: ${theme.colors.primary[500]};
  }
`;

const FoodList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FoodItem = styled.div`
  display: flex;
  gap: 1rem;
`;

const FoodIcon = styled.div<{ $type: 'good' | 'bad' }>`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$type === 'good' ? theme.colors.mint[100] : theme.colors.accent.coral}30;
  border-radius: ${theme.borderRadius.lg};
  color: ${props => props.$type === 'good' ? theme.colors.mint[600] : '#dc2626'};

  svg {
    width: 24px;
    height: 24px;
  }
`;

const FoodContent = styled.div`
  flex: 1;
`;

const FoodName = styled.div`
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.25rem;
`;

const FoodDescription = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.5;
`;

const TipCard = styled(Card)`
  background: linear-gradient(135deg, ${theme.colors.accent.sky}20 0%, white 100%);
  animation: slideUp 0.8s ease-out;
`;

const TipsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const TipItem = styled.li`
  padding-left: 1.5rem;
  position: relative;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.6;

  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: ${theme.colors.mint[500]};
    font-weight: ${theme.typography.fontWeight.bold};
  }
`;

const DietPage: React.FC = () => {
  const navigate = useNavigate();
  const { userProfile, getCurrentSession } = useAWSAuth();
  const { checkFeature, tier } = useSubscription();
  const [waterGlasses, setWaterGlasses] = useState(0);
  const [savedDietPlans, setSavedDietPlans] = useState<Array<DietPlan & { scanId: string; savedAt: string; scanAnalysis?: any; scanCustomName?: string }>>([]);
  const [latestScan, setLatestScan] = useState<any>(null);
  const [loadingScan, setLoadingScan] = useState(true);
  const [allScans, setAllScans] = useState<any[]>([]); // Store all scans to calculate scan numbers
  const targetGlasses = savedDietPlans.length > 0
    ? savedDietPlans[savedDietPlans.length - 1].waterIntakeGlasses
    : 8;

  useEffect(() => {
    // Load saved diet plans from localStorage (user-specific)
    if (!userProfile?.uid) return;
    const saved = JSON.parse(localStorage.getItem(`savedDietPlans_${userProfile.uid}`) || '[]');
    setSavedDietPlans(saved);
    // Set target glasses from most recent saved plan if available
    if (saved.length > 0) {
      const latest = saved[saved.length - 1];
      // Don't override user's current progress, just show the recommendation
    }
  }, [userProfile]);

  // Fetch latest scan for personalized food recommendations
  useEffect(() => {
    const fetchLatestScan = async () => {
      if (!userProfile?.uid || !getCurrentSession) {
        setLoadingScan(false);
        return;
      }

      try {
        const session = await getCurrentSession();
        if (!session) {
          setLoadingScan(false);
          return;
        }

        const response = await listUserScans(session, userProfile.uid);
        const completedScans = response.items.filter(s => s.status === 'complete');
        setAllScans(completedScans); // Store all scans for scan number calculation
        if (completedScans.length > 0) {
          // Sort by timestamp and get the latest
          const sorted = completedScans.sort((a, b) => {
            const timeA = new Date(a.createdAt ?? a.timestamp).getTime();
            const timeB = new Date(b.createdAt ?? b.timestamp).getTime();
            return timeB - timeA;
          });
          setLatestScan(sorted[0]);
        }
      } catch (error) {
        console.error('Error fetching latest scan:', error);
      } finally {
        setLoadingScan(false);
      }
    };

    fetchLatestScan();
  }, [userProfile, getCurrentSession]);

  // Generate personalized skin-loving foods
  const skinLovingFoods = useMemo(() => {
    return generateSkinLovingFoods(latestScan, userProfile?.dietaryPreference);
  }, [latestScan, userProfile?.dietaryPreference]);

  // Generate personalized foods to reduce
  const foodsToReduce = useMemo(() => {
    return generateFoodsToReduce(latestScan, userProfile?.dietaryPreference);
  }, [latestScan, userProfile?.dietaryPreference]);

  const addGlass = () => {
    if (waterGlasses < targetGlasses) {
      setWaterGlasses(waterGlasses + 1);
    }
  };

  const removeGlass = () => {
    if (waterGlasses > 0) {
      setWaterGlasses(waterGlasses - 1);
    }
  };

  const percentage = Math.round((waterGlasses / targetGlasses) * 100);


  return (
    <PageContainer>
      <Container>
        <Header>
          <Title>Diet & Nutrition 🥗</Title>
          <Subtitle>
            Feed your skin from the inside out. Simple nutrition tips for clear, glowing skin ✨
          </Subtitle>
          <Disclaimer>
            <strong>Note:</strong> This is wellness guidance, not medical advice. Always consult with a healthcare professional before making dietary changes.
          </Disclaimer>
        </Header>

        <HydrationCard padding="lg">
          <HydrationHeader>
            <HydrationTitle>
              <Droplet />
              Daily Hydration Tracker
            </HydrationTitle>
            <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
              {savedDietPlans.length > 0 
                ? (() => {
                    const plan = savedDietPlans[savedDietPlans.length - 1];
                    if (plan.waterIntakeDisplay) {
                      return `Recommended: ${plan.waterIntakeDisplay} per day (from your latest scan) 💧`;
                    }
                    return `Recommended: ${plan.waterIntakeGlasses} glasses per day (from your latest scan) 💧`;
                  })()
                : 'Target: 8 glasses (64 oz) per day 💧'}
            </p>
            {savedDietPlans.length > 0 && savedDietPlans[savedDietPlans.length - 1].waterIntakeSkinNote && (
              <p style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, marginTop: '0.25rem' }}>
                {savedDietPlans[savedDietPlans.length - 1].waterIntakeSkinNote}
              </p>
            )}
          </HydrationHeader>

          <GlassContainer>
            {[...Array(Math.max(8, savedDietPlans.length > 0 ? savedDietPlans[savedDietPlans.length - 1].waterIntakeGlasses : 8))].map((_, i) => (
              <GlassIcon
                key={i}
                $filled={i < waterGlasses}
                onClick={() => i < waterGlasses ? setWaterGlasses(i) : addGlass()}
              />
            ))}
          </GlassContainer>

          <HydrationControls>
            <Button variant="outline" onClick={removeGlass} disabled={waterGlasses === 0}>
              <Minus size={20} />
            </Button>
            <Button onClick={addGlass} disabled={waterGlasses === targetGlasses}>
              <Plus size={20} />
            </Button>
          </HydrationControls>

          <HydrationStats>
            <Stat>
              <StatNumber>{waterGlasses}/{targetGlasses}</StatNumber>
              <StatLabel>Glasses today</StatLabel>
            </Stat>
            <Stat>
              <StatNumber>{percentage}%</StatNumber>
              <StatLabel>Daily goal</StatLabel>
            </Stat>
            <Stat>
              <StatNumber>{waterGlasses * 8}oz</StatNumber>
              <StatLabel>Total intake</StatLabel>
            </Stat>
          </HydrationStats>
        </HydrationCard>

        <ContentGrid>
          <SectionCard padding="lg">
            <SectionTitle>
              <ThumbsUp />
              Skin-Loving Foods for your Specific Skin Type 💚
            </SectionTitle>
            <FoodList>
              {loadingScan ? (
                <FoodItem>
                  <FoodContent>
                    <FoodDescription>Loading personalized recommendations...</FoodDescription>
                  </FoodContent>
                </FoodItem>
              ) : skinLovingFoods.length > 0 ? (
                skinLovingFoods.map((food, idx) => {
                  let IconComponent: React.ReactNode;
                  switch (food.icon) {
                    case 'fish':
                      IconComponent = <Fish />;
                      break;
                    case 'salad':
                      IconComponent = <Salad />;
                      break;
                    case 'apple':
                      IconComponent = <Apple />;
                      break;
                    case 'nut':
                    case 'seed':
                      IconComponent = <span style={{ fontSize: '24px' }}>🥜</span>;
                      break;
                    case 'leaf':
                      IconComponent = <Salad />;
                      break;
                    case 'grain':
                      IconComponent = <span style={{ fontSize: '24px' }}>🌾</span>;
                      break;
                    case 'berry':
                      IconComponent = <Apple />;
                      break;
                    default:
                      IconComponent = <Apple />;
                  }
                  
                  return (
                    <FoodItem key={idx}>
                      <FoodIcon $type="good">
                        {IconComponent}
                      </FoodIcon>
                      <FoodContent>
                        <FoodName>{food.name}</FoodName>
                        <FoodDescription>{food.description}</FoodDescription>
                      </FoodContent>
                    </FoodItem>
                  );
                })
              ) : (
                <FoodItem>
                  <FoodContent>
                    <FoodDescription>Upload a scan to see personalized food recommendations for your skin type.</FoodDescription>
                  </FoodContent>
                </FoodItem>
              )}
            </FoodList>
          </SectionCard>

          <SectionCard padding="lg">
            <SectionTitle>
              <ThumbsDown />
              Foods to Reduce for your Specific Skin Type ⚠️
            </SectionTitle>
            <FoodList>
              {loadingScan ? (
                <FoodItem>
                  <FoodContent>
                    <FoodDescription>Loading personalized recommendations...</FoodDescription>
                  </FoodContent>
                </FoodItem>
              ) : foodsToReduce.length > 0 ? (
                foodsToReduce.map((food, idx) => {
                  let IconComponent: React.ReactNode;
                  switch (food.icon) {
                    case 'cake':
                      IconComponent = <span style={{ fontSize: '24px' }}>🍰</span>;
                      break;
                    case 'milk':
                      IconComponent = <span style={{ fontSize: '24px' }}>🥛</span>;
                      break;
                    case 'fries':
                      IconComponent = <span style={{ fontSize: '24px' }}>🍟</span>;
                      break;
                    case 'bread':
                      IconComponent = <span style={{ fontSize: '24px' }}>🍞</span>;
                      break;
                    case 'soda':
                      IconComponent = <span style={{ fontSize: '24px' }}>🥤</span>;
                      break;
                    case 'chocolate':
                      IconComponent = <span style={{ fontSize: '24px' }}>🍫</span>;
                      break;
                    case 'alcohol':
                      IconComponent = <span style={{ fontSize: '24px' }}>🍷</span>;
                      break;
                    case 'fastfood':
                      IconComponent = <span style={{ fontSize: '24px' }}>🍔</span>;
                      break;
                    default:
                      IconComponent = <span style={{ fontSize: '24px' }}>⚠️</span>;
                  }
                  
                  return (
                    <FoodItem key={idx}>
                      <FoodIcon $type="bad">
                        {IconComponent}
                      </FoodIcon>
                      <FoodContent>
                        <FoodName>{food.name}</FoodName>
                        <FoodDescription>{food.description}</FoodDescription>
                      </FoodContent>
                    </FoodItem>
                  );
                })
              ) : (
                <FoodItem>
                  <FoodContent>
                    <FoodDescription>Upload a scan to see personalized foods to reduce for your skin type.</FoodDescription>
                  </FoodContent>
                </FoodItem>
              )}
            </FoodList>
          </SectionCard>
        </ContentGrid>

        {/* Saved Diet Plans from Scans */}
        {savedDietPlans.length > 0 && (
          <SectionCard padding="lg" style={{ marginTop: '2rem' }}>
            {!checkFeature('customDietPlan') ? (
              <FeatureLock
                feature="Custom Diet Plans"
                requiredTier="pro"
                message="Personalized diet plans based on your scans are available in GlowLens Pro."
              />
            ) : (
            <>
            <SectionTitle>
              <Calendar />
              Personalized Diet Plans from Your Scans
            </SectionTitle>
            {savedDietPlans.map((plan, idx) => {
              // Calculate scan number based on chronological order
              const sortedScans = allScans
                .filter(s => s.status === 'complete')
                .sort((a, b) => {
                  const timeA = new Date(a.createdAt ?? a.timestamp).getTime();
                  const timeB = new Date(b.createdAt ?? b.timestamp).getTime();
                  return timeA - timeB; // Oldest first
                });
              const scanIndex = sortedScans.findIndex(s => s.scanId === plan.scanId);
              const scanNumber = scanIndex >= 0 ? scanIndex + 1 : undefined;
              
              // Find the actual scan record to get custom name
              const scanRecord = allScans.find(s => s.scanId === plan.scanId);
              const displayName = plan.scanCustomName || (scanRecord ? getScanDisplayName(scanRecord, scanNumber) : `Scan ${plan.scanId.substring(0, 8)}...`);
              
              return (
              <div key={idx} style={{ marginBottom: idx < savedDietPlans.length - 1 ? '2rem' : 0, padding: '1rem', background: theme.colors.background.secondary, borderRadius: theme.borderRadius.md }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <h4 style={{ margin: 0, fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold }}>
                    {displayName}
                  </h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.tertiary }}>
                      {new Date(plan.savedAt).toLocaleDateString()}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (userProfile?.uid && window.confirm('Are you sure you want to delete this diet plan?')) {
                          // Remove the diet plan from the array
                          const updatedPlans = savedDietPlans.filter((_, i) => i !== idx);
                          setSavedDietPlans(updatedPlans);
                          // Update localStorage
                          localStorage.setItem(`savedDietPlans_${userProfile.uid}`, JSON.stringify(updatedPlans));
                        }
                      }}
                      style={{ 
                        color: theme.colors.status.error,
                        borderColor: theme.colors.status.error,
                        padding: '0.375rem 0.75rem'
                      }}
                    >
                      <Trash2 size={14} />
                      Delete
                    </Button>
                  </div>
                </div>
                
                <div style={{ marginBottom: '1rem', padding: '0.75rem', background: theme.colors.primary[50], borderRadius: theme.borderRadius.md }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Droplet size={16} color={theme.colors.primary[600]} />
                      <strong style={{ fontSize: theme.typography.fontSize.sm }}>Water Intake:</strong>
                      <span style={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.primary[600] }}>
                        {plan.waterIntakeDisplay || `${plan.waterIntakeGlasses} glasses`}/day
                      </span>
                    </div>
                    {plan.waterIntakeSkinNote && (
                      <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>
                        {plan.waterIntakeSkinNote}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <h5 style={{ margin: '0 0 0.75rem 0', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.status.success }}>
                    Foods to Eat:
                  </h5>
                  {(() => {
                    // Filter meals and snacks by type - same logic as ResultsDetailPage
                    const allMeals = plan.foodsToEat.filter(food => {
                      if (food.type === 'meal') return true;
                      if (food.type === 'snack') return false;
                      const name = food.name.toLowerCase();
                      const isMealByName = name.includes('salad') && (name.includes('shrimp') || name.includes('chicken') || name.includes('salmon') || name.includes('turkey') || name.includes('beef')) ||
                                     name.includes('curry') || name.includes('stir-fry') || name.includes('bowl') || 
                                     name.includes('wrap') || name.includes('sandwich') || name.includes('burger') ||
                                     name.includes('pasta') || name.includes('rice') || name.includes('quinoa') ||
                                     name.includes('biryani') || name.includes('tikka') || name.includes('masala');
                      const calories = food.recipe?.caloriesPerServing ?? 0;
                      return isMealByName || calories >= 250;
                    });
                    
                    const allSnacks = plan.foodsToEat.filter(food => {
                      if (food.type === 'snack') return true;
                      if (food.type === 'meal') return false;
                      const calories = food.recipe?.caloriesPerServing ?? 0;
                      return calories > 0 && calories < 250;
                    });
                    
                    // Select first 3 meals and 3 snacks (upfront foods)
                    const upfrontMeals = allMeals.slice(0, 3);
                    const upfrontSnacks = allSnacks.slice(0, 3);
                    const upfrontFoods = [...upfrontMeals, ...upfrontSnacks];
                    
                    // Get remaining foods
                    const shownFoodIds = new Set(upfrontFoods.map(f => f.name));
                    const remainingFoods = plan.foodsToEat.filter(food => !shownFoodIds.has(food.name));
                    
                    return (
                      <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                          {/* Main Foods Section */}
                          {upfrontMeals.length > 0 && (
                            <>
                              <h6 style={{ margin: '0 0 0.75rem 0', fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text.primary }}>
                                Main Foods
                              </h6>
                              {upfrontMeals.map((food, i) => (
                                <div key={`meal-${i}`} style={{ padding: '1rem', background: theme.colors.background.secondary, borderRadius: theme.borderRadius.md, border: `1px solid ${theme.colors.border.light}` }}>
                                  <h5 style={{ margin: '0 0 0.5rem 0', fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.medium }}>
                                    {food.name}
                                  </h5>
                                  <p style={{ margin: '0 0 0.75rem 0', fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                                    {food.description}
                                  </p>
                                  {(() => {
                                    const recipe = getRecipeForDisplay(food);
                                    return (
                                      <details style={{ marginTop: '0.5rem' }}>
                                        <summary style={{ cursor: 'pointer', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.primary[600], display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                          <ChefHat size={16} />
                                          View Recipe: {recipe.title}
                                          {recipe.caloriesPerServing && ` (${recipe.caloriesPerServing} cal/serving)`}
                                        </summary>
                                        <div style={{ marginTop: '0.75rem', paddingLeft: '1rem' }}>
                                          {recipe.servings && (
                                            <p style={{ margin: '0 0 0.5rem 0', fontSize: theme.typography.fontSize.xs, color: theme.colors.text.tertiary }}>
                                              Servings: {recipe.servings}
                                            </p>
                                          )}
                                          <div style={{ marginBottom: '0.75rem' }}>
                                            <strong style={{ fontSize: theme.typography.fontSize.sm }}>Ingredients:</strong>
                                            <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem', fontSize: theme.typography.fontSize.sm }}>
                                              {(recipe.ingredients || []).map((ing, j) => (
                                                <li key={j}>{typeof ing === 'string' ? ing : String(ing)}</li>
                                              ))}
                                            </ul>
                                          </div>
                                          <div>
                                            <strong style={{ fontSize: theme.typography.fontSize.sm }}>Steps:</strong>
                                            <ol style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem', fontSize: theme.typography.fontSize.sm }}>
                                              {(recipe.steps || []).map((step, j) => (
                                                <li key={j} style={{ marginBottom: '0.25rem' }}>{typeof step === 'string' ? step : String(step)}</li>
                                              ))}
                                            </ol>
                                          </div>
                                        </div>
                                      </details>
                                    );
                                  })()}
                                </div>
                              ))}
                            </>
                          )}
                          
                          {/* Snacks Section */}
                          {upfrontSnacks.length > 0 && (
                            <>
                              <h6 style={{ margin: '1.5rem 0 0.75rem 0', fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text.primary }}>
                                Snacks
                              </h6>
                              {upfrontSnacks.map((food, i) => (
                                <div key={`snack-${i}`} style={{ padding: '1rem', background: theme.colors.background.secondary, borderRadius: theme.borderRadius.md, border: `1px solid ${theme.colors.border.light}` }}>
                                  <h5 style={{ margin: '0 0 0.5rem 0', fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.medium }}>
                                    {food.name}
                                  </h5>
                                  <p style={{ margin: '0 0 0.75rem 0', fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                                    {food.description}
                                  </p>
                                  {(() => {
                                    const recipe = getRecipeForDisplay(food);
                                    return (
                                      <details style={{ marginTop: '0.5rem' }}>
                                        <summary style={{ cursor: 'pointer', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.primary[600], display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                          <ChefHat size={16} />
                                          View Recipe: {recipe.title}
                                          {recipe.caloriesPerServing && ` (${recipe.caloriesPerServing} cal/serving)`}
                                        </summary>
                                        <div style={{ marginTop: '0.75rem', paddingLeft: '1rem' }}>
                                          {recipe.servings && (
                                            <p style={{ margin: '0 0 0.5rem 0', fontSize: theme.typography.fontSize.xs, color: theme.colors.text.tertiary }}>
                                              Servings: {recipe.servings}
                                            </p>
                                          )}
                                          <div style={{ marginBottom: '0.75rem' }}>
                                            <strong style={{ fontSize: theme.typography.fontSize.sm }}>Ingredients:</strong>
                                            <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem', fontSize: theme.typography.fontSize.sm }}>
                                              {(recipe.ingredients || []).map((ing, j) => (
                                                <li key={j}>{typeof ing === 'string' ? ing : String(ing)}</li>
                                              ))}
                                            </ul>
                                          </div>
                                          <div>
                                            <strong style={{ fontSize: theme.typography.fontSize.sm }}>Steps:</strong>
                                            <ol style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem', fontSize: theme.typography.fontSize.sm }}>
                                              {(recipe.steps || []).map((step, j) => (
                                                <li key={j} style={{ marginBottom: '0.25rem' }}>{typeof step === 'string' ? step : String(step)}</li>
                                              ))}
                                            </ol>
                                          </div>
                                        </div>
                                      </details>
                                    );
                                  })()}
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                        
                        {/* Show More Foods Button - Only for Pro+ plans */}
                        {(() => {
                          // Calculate remaining foods (same logic as above)
                          const allMeals = plan.foodsToEat.filter(food => {
                            if (food.type === 'meal') return true;
                            if (food.type === 'snack') return false;
                            const name = food.name.toLowerCase();
                            const isMealByName = name.includes('salad') && (name.includes('shrimp') || name.includes('chicken') || name.includes('salmon') || name.includes('turkey') || name.includes('beef')) ||
                                           name.includes('curry') || name.includes('stir-fry') || name.includes('bowl') || 
                                           name.includes('wrap') || name.includes('sandwich') || name.includes('burger') ||
                                           name.includes('pasta') || name.includes('rice') || name.includes('quinoa') ||
                                           name.includes('biryani') || name.includes('tikka') || name.includes('masala');
                            const calories = food.recipe?.caloriesPerServing ?? 0;
                            return isMealByName || calories >= 250;
                          });
                          
                          const allSnacks = plan.foodsToEat.filter(food => {
                            if (food.type === 'snack') return true;
                            if (food.type === 'meal') return false;
                            const calories = food.recipe?.caloriesPerServing ?? 0;
                            return calories > 0 && calories < 250;
                          });
                          
                          const upfrontMeals = allMeals.slice(0, 3);
                          const upfrontSnacks = allSnacks.slice(0, 3);
                          const upfrontFoods = [...upfrontMeals, ...upfrontSnacks];
                          
                          const shownFoodIds = new Set(upfrontFoods.map(f => f.name));
                          const remainingFoods = plan.foodsToEat.filter(food => !shownFoodIds.has(food.name));
                          
                          // Calculate remaining meals and snacks separately
                          const remainingMeals = remainingFoods.filter(food => {
                            if (food.type === 'meal') return true;
                            if (food.type === 'snack') return false;
                            const calories = food.recipe?.caloriesPerServing ?? 0;
                            return calories >= 250;
                          });
                          const remainingSnacks = remainingFoods.filter(food => {
                            if (food.type === 'snack') return true;
                            if (food.type === 'meal') return false;
                            const calories = food.recipe?.caloriesPerServing ?? 0;
                            return calories > 0 && calories < 250;
                          });
                          
                          // For Pro and Elite: Show exactly 6 meals and 3 snacks (9 total)
                          // We show 3 upfront, so we need 3 more meals and 0 more snacks = 6 more total
                          // But we want to show "9 more" to indicate 6 meals + 3 snacks will be available
                          const totalToShow = 9; // 6 meals + 3 snacks total
                          const upfrontCount = upfrontFoods.length; // 3 meals + 3 snacks = 6 upfront
                          const moreToShow = totalToShow - upfrontCount; // 9 - 6 = 3 more needed
                          
                          // But actually, we want to show that there will be 6 meals and 3 snacks total
                          // So if we show 3 meals upfront, we need 3 more meals (and 0 more snacks since we already show 3)
                          // So the button should say "9 more" meaning 6 meals + 3 snacks total will be shown
                          // Actually, let me recalculate: we show 3 meals + 3 snacks = 6 upfront
                          // We want to show 6 meals + 3 snacks = 9 total
                          // So we need 3 more meals, 0 more snacks = 3 more items
                          // But the user wants it to say "9 more" which means total items that will be shown
                          // Wait, the user said "it should be saying 'Show More Foods (9 more)'"
                          // So they want the button to always say "9 more" regardless
                          
                          return remainingFoods.length > 0 && (tier === 'pro' || tier === 'elite') ? (
                            <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  navigate('/diet-plan-foods', {
                                    state: { 
                                      dietPlan: {
                                        ...plan,
                                        foodsToEat: remainingFoods
                                      },
                                      scanAnalysis: plan.scanAnalysis,
                                      upfrontFoods: upfrontFoods
                                    }
                                  });
                                }}
                              >
                                Show More Foods (9 more)
                                <ArrowRight size={16} style={{ marginLeft: '0.5rem' }} />
                              </Button>
                            </div>
                          ) : null;
                        })()}
                      </>
                    );
                  })()}
                </div>

                <div>
                  <h5 style={{ margin: '0 0 0.5rem 0', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.status.error }}>
                    Foods to Avoid:
                  </h5>
                  {plan.foodsToAvoid.map((food, i) => (
                    <div key={i} style={{ marginBottom: '0.5rem', fontSize: theme.typography.fontSize.xs }}>
                      <strong>{food.name}:</strong> <span style={{ color: theme.colors.text.secondary }}>{food.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
              );
            })}
            </>
            )}
          </SectionCard>
        )}

        <div style={{ marginTop: '1.5rem' }}>
          <TipCard padding="lg">
            <SectionTitle>Quick Wellness Tips 💡</SectionTitle>
          <TipsList>
            <TipItem>
              <strong>Balance, not perfection:</strong> You don't need to eliminate foods completely. Focus on adding more skin-loving foods.
            </TipItem>
            <TipItem>
              <strong>Listen to your body:</strong> Food sensitivities vary. Keep a food diary to identify your triggers.
            </TipItem>
            <TipItem>
              <strong>Timing matters:</strong> Drink water throughout the day, not all at once. Aim for 8oz every 1-2 hours.
            </TipItem>
            <TipItem>
              <strong>Gut health = skin health:</strong> Probiotics (yogurt, kimchi, kombucha) support both digestion and clear skin.
            </TipItem>
            <TipItem>
              <strong>Be patient:</strong> Dietary changes take 4-6 weeks to show results in your skin. Stay consistent! 🌱
            </TipItem>
          </TipsList>
          </TipCard>
        </div>
      </Container>
    </PageContainer>
  );
};

export default DietPage;
